import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  BEFORE_INPUT_COMMAND,
  COMMAND_PRIORITY_HIGH,
  CONTROLLED_TEXT_INSERTION_COMMAND,
  DELETE_CHARACTER_COMMAND,
  DRAGSTART_COMMAND,
  DROP_COMMAND,
  INSERT_LINE_BREAK_COMMAND,
  KEY_DOWN_COMMAND,
  KEY_ENTER_COMMAND,
  PASTE_COMMAND,
  TextNode,
  type ElementNode,
  type LexicalNode,
  type PasteCommandType,
  type RangeSelection,
} from 'lexical';
import { FC, useEffect } from 'react';

import {
  $isTemplateSubheading,
  $placeTemplateInstruction,
  $selectTemplateBlock,
  createTemplateSlotDef,
} from '../../../lib';
import {
  $createTemplateInstructionNode,
  $createTemplateParagraphNode,
  $isTemplateFieldNode,
  $isTemplateHeadingNode,
  $isTemplateInstructionNode,
  $isTemplateParagraphNode,
  TEMPLATE_INSTRUCTION_TYPED_COMMAND,
  TemplateInstructionNode,
} from '../nodes';

const readPastedText = (event: PasteCommandType) => {
  const data =
    'clipboardData' in event
      ? event.clipboardData
      : 'dataTransfer' in event
        ? event.dataTransfer
        : null;
  return data?.getData('text/plain') ?? '';
};

// Pasted text comes in as text alone, so a paste never repeats a field's
// id; its lines are lines of the template, and a title takes one line.
const $pastePlainText = (event: PasteCommandType) => {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return false;
  }
  event.preventDefault();
  const text = readPastedText(event).replace(/\t/g, ' ');
  const inTitle = $isTemplateHeadingNode(
    selection.anchor.getNode().getTopLevelElement(),
  );
  const parts = inTitle ? [text.replace(/\s+/g, ' ')] : text.split(/\r?\n/);
  parts.forEach((part, index) => {
    const current = $getSelection();
    if (!$isRangeSelection(current)) {
      return;
    }
    if (index > 0) {
      current.insertParagraph();
    }
    const next = $getSelection();
    if ($isRangeSelection(next) && part) {
      next.insertText(part);
    }
  });
  return true;
};

// The node right next to a collapsed caret, in the direction of a delete.
const $readNodeNext = (selection: RangeSelection, isBackward: boolean) => {
  const { anchor } = selection;
  const node = anchor.getNode();
  if (anchor.type === 'element') {
    return $isElementNode(node)
      ? node.getChildAtIndex(isBackward ? anchor.offset - 1 : anchor.offset)
      : null;
  }
  const atEdge = isBackward
    ? anchor.offset === 0
    : anchor.offset === node.getTextContentSize();
  if (!atEdge) {
    return null;
  }
  return isBackward ? node.getPreviousSibling() : node.getNextSibling();
};

const isTemplateBlock = (node: LexicalNode | null) =>
  $isTemplateFieldNode(node) || $isTemplateInstructionNode(node);

const $isAtBlockEdge = (
  selection: RangeSelection,
  block: ElementNode,
  isBackward: boolean,
) => {
  const { anchor } = selection;
  const node = anchor.getNode();
  if (isBackward) {
    return (
      anchor.offset === 0 &&
      (node.is(block) || node.is(block.getFirstDescendant()))
    );
  }
  return node.is(block)
    ? anchor.offset === block.getChildrenSize()
    : node.is(block.getLastDescendant()) &&
        anchor.offset === node.getTextContentSize();
};

// A summary's or an evaluation's field keeps its title and its instruction
// apart: a delete at their border joins neither to the other.
const $isAtFieldBorder = (selection: RangeSelection, isBackward: boolean) => {
  const block = selection.anchor.getNode().getTopLevelElement();
  const sibling = isBackward
    ? block?.getPreviousSibling()
    : block?.getNextSibling();
  return (
    !!block &&
    !!sibling &&
    $isTemplateHeadingNode(sibling) !== $isTemplateHeadingNode(block) &&
    $isAtBlockEdge(selection, block, isBackward)
  );
};

// Enter in a field's title goes on to its instruction under it.
const $enterInstruction = () => {
  const selection = $getSelection();
  const heading = $isRangeSelection(selection)
    ? selection.anchor.getNode().getTopLevelElement()
    : null;
  if (!$isTemplateHeadingNode(heading)) {
    return false;
  }
  const next = heading.getNextSibling();
  if (next && !$isTemplateHeadingNode(next) && $isElementNode(next)) {
    next.selectStart();
  } else {
    heading.insertAfter($createTemplateParagraphNode()).selectStart();
  }
  return true;
};

// `[` … `]` typed in a line becomes an AI instruction: the run that `]`
// closes, just typed, turns into one, and the caret goes on after it, or
// into it while it is empty. Brackets a template was read with stay text. A
// summary's or an evaluation's field takes one alone under its title only.
const $convertTypedInstruction = (node: TextNode, fields: boolean) => {
  const selection = $getSelection();
  if (
    !$isRangeSelection(selection) ||
    !selection.isCollapsed() ||
    selection.anchor.key !== node.getKey()
  ) {
    return null;
  }
  const line = node.getTopLevelElement();
  if (!$isTemplateParagraphNode(line) || $isTemplateSubheading(line)) {
    return null;
  }
  const end = selection.anchor.offset;
  const match = /\[([^[\]]*)\]$/.exec(node.getTextContent().slice(0, end));
  if (!match) {
    return null;
  }
  const start = end - match[0].length;
  const text = node.getTextContent();
  const alone =
    line.getChildrenSize() === 1 &&
    !`${text.slice(0, start)}${text.slice(end)}`.trim() &&
    $isTemplateHeadingNode(line.getPreviousSibling());
  if (fields && !alone) {
    return null;
  }
  const parts = node.splitText(start, end);
  const run = start === 0 ? parts[0] : parts[1];
  const def = createTemplateSlotDef(match[1].trim());
  const slot = $createTemplateInstructionNode(def);
  run.replace(slot);
  slot.selectNext(0, 0);
  return def.AIInstruction ? null : def.ID;
};

type Props = {
  fields: boolean;
};

// The few keys the template gives a meaning of its own; everything else is
// Lexical's rich text.
export const TemplateKeysPlugin: FC<Props> = ({ fields }) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const typed = { bracket: false };
    const watchBracket = (data: string | null | undefined) => {
      typed.bracket = !!data?.endsWith(']');
      return false;
    };
    return mergeRegister(
      editor.registerCommand(
        PASTE_COMMAND,
        $pastePlainText,
        COMMAND_PRIORITY_HIGH,
      ),
      editor.registerCommand(
        INSERT_LINE_BREAK_COMMAND,
        () => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) {
            return false;
          }
          const block = selection.anchor.getNode().getTopLevelElement();
          if ($isTemplateHeadingNode(block)) {
            return true;
          }
          if (fields) {
            return false;
          }
          selection.insertParagraph();
          return true;
        },
        COMMAND_PRIORITY_HIGH,
      ),
      // Backspace or Delete next to a field selects it first; the next one
      // deletes it.
      editor.registerCommand(
        DELETE_CHARACTER_COMMAND,
        (isBackward) => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
            return false;
          }
          const next = $readNodeNext(selection, isBackward);
          if (next && isTemplateBlock(next)) {
            $selectTemplateBlock(next.getKey());
            return true;
          }
          return fields && $isAtFieldBorder(selection, isBackward);
        },
        COMMAND_PRIORITY_HIGH,
      ),
      ...[DROP_COMMAND, DRAGSTART_COMMAND].map((command) =>
        editor.registerCommand(
          command,
          (event: DragEvent) => {
            event.preventDefault();
            return true;
          },
          COMMAND_PRIORITY_HIGH,
        ),
      ),
      ...(fields
        ? [
            editor.registerCommand(
              KEY_ENTER_COMMAND,
              (event) => {
                const handled = $enterInstruction();
                if (handled) {
                  event?.preventDefault();
                }
                return handled;
              },
              COMMAND_PRIORITY_HIGH,
            ),
          ]
        : []),
      editor.registerCommand(
        KEY_DOWN_COMMAND,
        (event) => watchBracket(event.key),
        COMMAND_PRIORITY_HIGH,
      ),
      editor.registerCommand(
        BEFORE_INPUT_COMMAND,
        (event) => watchBracket(event.data),
        COMMAND_PRIORITY_HIGH,
      ),
      editor.registerCommand(
        CONTROLLED_TEXT_INSERTION_COMMAND,
        (payload) =>
          watchBracket(typeof payload === 'string' ? payload : payload.data),
        COMMAND_PRIORITY_HIGH,
      ),
      editor.registerNodeTransform(TextNode, (node) => {
        const empty = typed.bracket && $convertTypedInstruction(node, fields);
        if (empty) {
          typed.bracket = false;
          editor.dispatchCommand(TEMPLATE_INSTRUCTION_TYPED_COMMAND, empty);
        }
      }),
      editor.registerNodeTransform(
        TemplateInstructionNode,
        $placeTemplateInstruction,
      ),
      // The key, its input and the text it puts in come in updates of their
      // own: the mark holds until text changes.
      editor.registerUpdateListener(({ dirtyLeaves }) => {
        if (dirtyLeaves.size) {
          typed.bracket = false;
        }
      }),
    );
  }, [editor, fields]);

  return null;
};
